import React, { useMemo, useState, useEffect } from 'react'
import BigNumber from 'bignumber.js'
import useI18n from 'hooks/useI18n'
import styled, { keyframes } from 'styled-components'
import { Flex, Text, LinkExternal, Link, CopyIcon } from '@pancakeswap-libs/uikit'
import { CopyToClipboard } from 'react-copy-to-clipboard';
import CardHeading from './CardHeading'
import CardActionsContainer from './LiquidityGeneratorTokenCardActionsContainer'

const FCard = styled.div`
  align-self: baseline;
  background: ${(props) => props.theme.card.background};
  border-radius: 32px;
  box-shadow: 0px 2px 12px -8px rgba(25, 19, 38, 0.1), 0px 1px 1px rgba(25, 19, 38, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  padding: 24px;
  position: relative;
  text-align: center;
`

const StyledText = styled(Text)`
    line-height: 28px;
`

const StyledLinkExternalBase = styled(LinkExternal)`
  line-height: 28px;
`

const StyledLinkExternal = styled(StyledLinkExternalBase)`
  text-decoration: none;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;

  svg {
    padding-left: 4px;
    height: 18px;
    width: auto;
    fill: ${({ theme }) => theme.colors.primary};
  }
`

const Row2 = styled.div`
  text-align: right;
`

const StyledText2 = styled(StyledText)`
  margin-right: 5px;
  display: inline-block;
`

const StyledLinkExternal2 = styled(LinkExternal)`
  text-decoration: none;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.text};
  display: inline-block;
  position: relative;

  svg {
    position: relative;
    top: 3px;
    display: inline-block;
    vertical-align: top;
    padding-left: 4px;
    height: 18px;
    width: auto;
    fill: ${({ theme }) => theme.colors.primary};
  }
`

const Divider = styled.div`
  background-color: ${({ theme }) => theme.colors.borderColor};
  height: 1px;
  margin: 28px auto;
  width: 100%;
`

const StyledCopyToClipboard = styled(CopyToClipboard)`
  color: #f98c08;
  line-height: 28px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const CopySuccessText = styled.div`
    color: #2aa339fa;
    margin-left: 20px;
    line-height: 28px;
`

export interface CardProps {
  token: any,
  account?: string
}

const getDisplayBalance = (balance) => {
    const displayBalanceOption = balance > 1 ? {} : {maximumFractionDigits: 12};
    const displayBalance = navigator ? balance.toLocaleString(navigator.language, displayBalanceOption) : balance.toLocaleString();
    return displayBalance;
}

const Card: React.FC<CardProps> = ({ token, account }) => {
  const TranslateString = useI18n();
  const { address, owner, name, symbol, decimals, supply, burned, circulating, taxFeeRate, liquidityFeeRate, charityFeeRate, charityAddress } = token;
  const isOwner = account && owner && owner.toLowerCase() === account.toLowerCase();
  const hasCharityAddress = charityAddress && (new BigNumber(charityAddress.toLowerCase())).isGreaterThan(0);

  const displaySupply = getDisplayBalance(supply.toNumber());
  const displayBurned = getDisplayBalance(burned.toNumber());
  const displayCirculating = getDisplayBalance(circulating.toNumber());

  const [showCopySuccess1, setShowCopySuccess1] = useState(false);
  const [showCopySuccess2, setShowCopySuccess2] = useState(false);

  const copying1 = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!showCopySuccess1) document.dispatchEvent(new Event("beforeCopyAnything"));
    setShowCopySuccess1(true)
  }

  const copying2 = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!showCopySuccess2) document.dispatchEvent(new Event("beforeCopyAnything"));
    setShowCopySuccess2(true)
  }
  
  useEffect(() => {
    const handleClickOutside = () => {
      setShowCopySuccess1(false)
      setShowCopySuccess2(false)
    }

    document.addEventListener('click', handleClickOutside)
    document.addEventListener('beforeCopyAnything', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('beforeCopyAnything', handleClickOutside)
    }
  }, [])

  return (
    <FCard>
      <CardHeading token={token} />
      <Flex justifyContent='space-between'>
        <StyledText>{TranslateString(418,'Type')}:</StyledText>
        <StyledText>{TranslateString(523,'Liquidity Generator')}</StyledText>
      </Flex>
      <Flex justifyContent='space-between'>
        <StyledText>{TranslateString(419,'Supply')}:</StyledText>
        <StyledText>{displaySupply}</StyledText>
      </Flex>
      <Flex justifyContent='space-between'>
        <StyledText>{TranslateString(422,'Burned')}:</StyledText>
        <StyledText>{displayBurned}</StyledText>
      </Flex>
      <Flex justifyContent='space-between'>
        <StyledText>{TranslateString(421,'Circulating')}:</StyledText>
        <StyledText>{displayCirculating}</StyledText>
      </Flex>
      <Flex justifyContent='space-between'>
        <StyledText>{TranslateString(1042,'Transaction Fee')}:</StyledText>
        <StyledText>{taxFeeRate+liquidityFeeRate+charityFeeRate}%</StyledText>
      </Flex>
      {
        !!taxFeeRate && (
          <Flex justifyContent='space-between'>
            <StyledText>{TranslateString(1043,'Yield')}:</StyledText>
            <StyledText>{taxFeeRate}%</StyledText>
          </Flex>
        )
      }
      {
        !!liquidityFeeRate && (
          <Flex justifyContent='space-between'>
            <StyledText>{TranslateString(1044,'Liquidity')}:</StyledText>
            <StyledText>{liquidityFeeRate}%</StyledText>
          </Flex>
        )
      }
      {
        hasCharityAddress && !!charityFeeRate && (
          <Flex justifyContent='space-between'>
            <StyledText>{TranslateString(1045,'Charity/Marketing')}:</StyledText>
            <StyledText>{charityFeeRate}%</StyledText>
          </Flex>
        )
      }
      {
        hasCharityAddress && (
          <Flex justifyContent="space-between">
            <StyledText>{TranslateString(1046,'C/M Address')}:</StyledText>
            <StyledLinkExternal href={`https://smartscout.cash/address/${charityAddress}`}>
              {charityAddress && charityAddress.substring(0, 6)}
            </StyledLinkExternal>
          </Flex>
        )
      }
      <Flex justifyContent="space-between">
        <StyledText>{TranslateString(117,'Owner')}:</StyledText>
        <StyledLinkExternal href={`https://smartscout.cash/address/${owner}`}>
          {owner && owner.substring(0, 6)}
        </StyledLinkExternal>
      </Flex>
      { account ? <Divider/> : "" }
      { account ? <CardActionsContainer token={token} account={account} /> : "" }
      <Divider/>
      <Flex justifyContent="flex-start" onClick={(e)=>{e.stopPropagation();}}>
        <StyledCopyToClipboard text={address} onCopy={copying1} title={TranslateString(1316, 'Copy Token Address')}>
          <span>
            {TranslateString(1316, 'Copy Token Address')}
            <CopyIcon style={{ verticalAlign: 'middle', marginLeft: 4, marginBottom: 3, cursor: 'pointer', display: 'inline-block', fill: '#f98c08' }} />
          </span>
        </StyledCopyToClipboard>
        {showCopySuccess1 && <CopySuccessText>{TranslateString(1315, 'Copied!')}</CopySuccessText>}
      </Flex>
      <Flex justifyContent="flex-start" onClick={(e)=>{e.stopPropagation();}}>
        <StyledCopyToClipboard text={`https://benswap.cash/sep20?token=${address}`} onCopy={copying2} title={TranslateString(1317, 'Copy Link')}>
          <span>
            {TranslateString(1317, 'Copy Link')}
            <CopyIcon style={{ verticalAlign: 'middle', marginLeft: 4, marginBottom: 3, cursor: 'pointer', display: 'inline-block', fill: '#f98c08' }} />
          </span>
        </StyledCopyToClipboard>
        {showCopySuccess2 && <CopySuccessText>{TranslateString(1315, 'Copied!')}</CopySuccessText>}
      </Flex>
      <Flex justifyContent="flex-start">
        <StyledLinkExternalBase href={`https://dexfull.benswap.cash/#/swap/${address}`} bold={false}>
          {TranslateString(567, 'Get')} {token.symbol}
        </StyledLinkExternalBase>
      </Flex>
      <Flex justifyContent="flex-start">
        <StyledLinkExternalBase href={`https://smartscout.cash/address/${address}`} bold={false}>
            {TranslateString(95,'View on Smartscout')}
        </StyledLinkExternalBase>
      </Flex>
    </FCard>
  )
}

export default Card
